import type { ConsolidatedChange } from '@/hooks/useProposalEditor'

type CurrentEntityValues = {
  event: Record<string, any>
  edition: Record<string, any>
  organizer: { name?: any; websiteUrl?: any; facebookUrl?: any; instagramUrl?: any } | null
} | null | undefined

// Champs injectables = ceux que TOUTE la chaîne d'application porte réellement
// (blockFieldMapping ∩ filterChangesByBlock backend ∩ routage domaine correct).
// On reste volontairement conservateur : un champ affiché comme éditable DOIT
// pouvoir être enregistré. Élargir ces listes nécessite d'élargir aussi
// blockFieldMapping.ts, filterChangesByBlock (apps/api) et le routage du
// ProposalDomainService — sinon l'édition serait silencieusement perdue.
const EVENT_FIELDS = [
  'name', 'city', 'country',
  'countrySubdivisionNameLevel1', 'countrySubdivisionNameLevel2',
  'websiteUrl', 'facebookUrl', 'instagramUrl', 'twitterUrl'
]
const EDITION_FIELDS = [
  'year', 'startDate', 'endDate', 'calendarStatus', 'timeZone',
  'registrationOpeningDate', 'registrationClosingDate', 'registrantsNumber'
]

const ELIGIBLE_TYPES = new Set(['NEW_EVENT', 'EDITION_UPDATE'])

/**
 * Complète une liste de ConsolidatedChange avec tous les champs Event/Edition
 * non encore présents (issus de fieldCategories), plus un champ `organizer`
 * synthétique. Les champs injectés portent options=[] et isUnproposed=true,
 * et leur currentValue vient de currentEntityValues (null pour NEW_EVENT).
 *
 * Hors périmètre (autres types) : retourne la liste inchangée.
 */
export function injectUnproposedFields(
  changes: ConsolidatedChange[],
  currentEntityValues: CurrentEntityValues,
  proposalType: string
): ConsolidatedChange[] {
  if (!ELIGIBLE_TYPES.has(proposalType)) return changes

  const present = new Set(changes.map(c => c.field))
  const result = [...changes]

  const inject = (field: string, currentValue: any) => {
    if (present.has(field)) return
    present.add(field)
    result.push({ field, options: [], currentValue: currentValue ?? null, selectedValue: undefined, isUnproposed: true })
  }

  for (const field of EVENT_FIELDS) inject(field, currentEntityValues?.event?.[field])
  for (const field of EDITION_FIELDS) inject(field, currentEntityValues?.edition?.[field])
  inject('organizer', currentEntityValues?.organizer)

  return result
}
